import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Box, Collapse, Grow, IconButton } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { setShowToastEmitter, clearShowToastEmitter } from "../../../services/toastBus";
import { TOAST_LAYOUT } from "../../../constants/keywords";

const ALERT_COLORS = {
  success: { backgroundColor: "#D3FCD2", color: "#065E49" },
  error: { backgroundColor: "#FFE9D5", color: "#7A0916" },
  warning: { backgroundColor: "#FFF7E6", color: "#7A4100" },
  info: { backgroundColor: "#E6FCFF", color: "#003768" },
};

const ICON_SX = { fontSize: 20 };
const ALERT_ICONS = {
  success: <CheckCircleIcon sx={{ ...ICON_SX, color: "#22C55E" }} />,
  error: <ErrorIcon sx={{ ...ICON_SX, color: "#FF5630" }} />,
  warning: <WarningRoundedIcon sx={{ ...ICON_SX, color: "#FFAB00" }} />,
  info: <InfoOutlinedIcon sx={{ ...ICON_SX, color: "#00B8D9" }} />,
};

let nextToastId = 0;

const ToastProvider = () => {
  const [toasts, setToasts] = useState([]);
  const [stackTop, setStackTop] = useState(TOAST_LAYOUT.STACK_TOP_DEFAULT_PX);
  const autoHideTimers = useRef(new Map());

  useEffect(() => {
    const el = document.querySelector(`.${TOAST_LAYOUT.TOP_NOTICE_CLASS}`);
    if (!el) return undefined;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      setStackTop(
        rect.height > 0 ? rect.bottom + TOAST_LAYOUT.STACK_GAP_PX : TOAST_LAYOUT.STACK_TOP_DEFAULT_PX
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const dismiss = useCallback(
    (id) => {
      const timer = autoHideTimers.current.get(id);
      if (timer) {
        clearTimeout(timer);
        autoHideTimers.current.delete(id);
      }
      setToasts((prev) =>
        prev.map((toast) => (toast.id === id ? { ...toast, closing: true } : toast))
      );
    },
    []
  );

  const enqueue = useCallback(
    (message, alertType, toastDuration, action) => {
      const id = ++nextToastId;
      const duration = toastDuration || TOAST_LAYOUT.DEFAULT_DURATION_MS;
      setToasts((prev) => [
        ...prev,
        {
          id,
          message,
          alertType: (alertType || "error").toLowerCase(),
          action,
          closing: false,
        },
      ]);
      autoHideTimers.current.set(
        id,
        setTimeout(() => dismiss(id), duration)
      );
    },
    [dismiss]
  );

  useEffect(() => {
    setShowToastEmitter(enqueue);
    return () => clearShowToastEmitter();
  }, [enqueue]);

  useEffect(() => {
    const timers = autoHideTimers.current;
    return () => {
      timers.forEach(clearTimeout);
      timers.clear();
    };
  }, []);

  if (toasts.length === 0) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: stackTop,
        right: TOAST_LAYOUT.RIGHT_OFFSET_PX,
        zIndex: (theme) => theme.zIndex.snackbar,
        display: "flex",
        flexDirection: "column",
        width: TOAST_LAYOUT.TOAST_WIDTH_PX,
        pointerEvents: "none",
      }}
    >
      {toasts.map((toast) => {
        const closeButton = (
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            sx={{ padding: "4px" }}
            onClick={() => dismiss(toast.id)}
          >
            <CloseIcon sx={{ width: "16px", height: "16px" }} />
          </IconButton>
        );
        return (
          <Collapse
            key={toast.id}
            in={!toast.closing}
            timeout={TOAST_LAYOUT.EXIT_TRANSITION_MS}
            onExited={() => removeToast(toast.id)}
            sx={{ mb: `${TOAST_LAYOUT.STACK_GAP_PX}px` }}
          >
            <Grow in={!toast.closing} timeout={TOAST_LAYOUT.EXIT_TRANSITION_MS}>
              <Alert
                severity={toast.alertType}
                variant="standard"
                icon={ALERT_ICONS[toast.alertType]}
                action={
                  toast.action ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      {toast.action}
                      {closeButton}
                    </Box>
                  ) : (
                    closeButton
                  )
                }
                sx={{
                  width: "100%",
                  pointerEvents: "auto",
                  display: "flex",
                  alignItems: "flex-start",
                  paddingTop: "6px",
                  paddingBottom: "6px",
                  backgroundColor: ALERT_COLORS[toast.alertType]?.backgroundColor || "inherit",
                  color: ALERT_COLORS[toast.alertType]?.color || "inherit",
                  "& .MuiAlert-icon": {
                    padding: 0,
                    alignSelf: "flex-start",
                    height: "24px",
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiAlert-message": {
                    padding: 0,
                    lineHeight: "24px",
                  },
                  "& .MuiAlert-action": {
                    padding: "0 0 0 16px",
                    alignSelf: "flex-start",
                  },
                }}
              >
                {toast.message}
              </Alert>
            </Grow>
          </Collapse>
        );
      })}
    </Box>
  );
};

export default ToastProvider;
